/**
 * The heart of the ledger: turning a stored client row into everything the UI
 * needs to draw it — parsed dates, day counts, a lifecycle percentage, and the
 * list of flags that put it in the action queue.
 *
 * `deriveClient` is called for every row on every render, so it stays cheap:
 * plain arithmetic and date parsing, no allocation beyond the result.
 */
import {
  parseDate, startOfDay, daysTo, daysAgo, periodDays, addPeriod, formatDate
} from '../lib/dates.js';
import { money, lower } from '../lib/format.js';
import {
  priceOf, amountOf, isBilled, moduleList, isBundled, mrrOf, planAmount, statedAmount, cycleMonths
} from './pricing.js';
import { PRESET_FLAGS, LAPSED_DAYS, PROMO_ANY, PROMO_NONE } from '../constants/defaults.js';

const NO_TRIAL = 'No trial';

/** Classify an account when the spreadsheet did not say. */
export function inferType(S, r) {
  const plan = String(r.plan || '').toUpperCase();
  if (/internal|test|demo/i.test(`${r.handler || ''} ${r.name || ''}`) && S.types.includes('Internal')) {
    return 'Internal';
  }
  if (lower(S.paidValues).includes(String(r.pay || '').toLowerCase())) return 'Paying';
  if (plan === 'TRIAL' || parseDate(r.trialEnd)) return 'Trial';
  return S.types.includes('Free') ? 'Free' : S.types[0];
}

/**
 * When is the next payment due? An explicit date wins. Otherwise roll forward
 * from the last payment (or trial end, or registration) one period at a time
 * until the date is in the future.
 */
export function nextDueOf(S, r) {
  const explicit = parseDate(r.nextDue);
  if (explicit) return explicit;
  if (!periodDays(r.cycle)) return null;

  const base = parseDate(r.lastPaid) || parseDate(r.trialEnd) || parseDate(r.regOn);
  if (!base) return null;

  const today = startOfDay(new Date());
  let guess = new Date(base);
  let guard = 0;
  while (startOfDay(guess) <= today && guard < 400) {
    guess = addPeriod(guess, r.cycle);
    guard++;
  }
  return guess;
}

/**
 * Everything derived from one client row.
 *
 * Flags carry a severity (3 urgent, 2 warning, 1 informational) and a tag the
 * alert engine and automation rules match on.
 */
/*
 * One answer per row, kept until the row changes.
 *
 * `deriveClient` parses six dates, builds the flag list and works out the
 * lifecycle bar. Cheap once — 8ms over the whole book — and it was being run
 * five or six times for the same row in a single render: the sidebar badges,
 * the tiles, the filter, the sort, and again in the table body.
 *
 * Keyed on the row object itself. Rows are replaced rather than mutated, so a
 * changed row is a different object and misses the cache on its own; a
 * WeakMap means a row that leaves the book takes its entry with it. The
 * settings are checked too, because a threshold somebody edits in Settings
 * changes what stalled means for every row at once.
 */
const DERIVED = new WeakMap();
let derivedFor = null;

export function deriveClient(S, r) {
  if (!r || typeof r !== 'object') return rawDerive(S, r);
  if (derivedFor !== S) { DERIVED.clear?.(); derivedFor = S; }
  const hit = DERIVED.get(r);
  if (hit && hit.S === S) return hit.v;
  const v = rawDerive(S, r);
  DERIVED.set(r, { S, v });
  return v;
}

function rawDerive(S, r) {
  const type = r.acctType || inferType(S, r);
  // Accept either key: the settings screen writes `excluded`, older
  // workspaces and the server default carry `excludedTypes`.
  const excluded = (S.excluded || S.excludedTypes || []).includes(type);

  const reg = parseDate(r.regOn);
  const exp = parseDate(r.expiry);
  const act = parseDate(r.lastActivity);
  const con = parseDate(r.lastContacted);

  const noTrial = String(r.trialEnd || '').trim().toLowerCase() === NO_TRIAL.toLowerCase();
  const trial = noTrial ? null : parseDate(r.trialEnd);

  const dT = daysTo(trial);
  const dE = daysTo(exp);

  const paid = lower(S.paidValues).includes(String(r.pay || '').toLowerCase()) || type === 'Paying';
  const live = lower(S.liveStatuses).includes(String(r.status || '').toLowerCase());
  /*
   * Days since they last opened XYRA. `null` when the app has never seen them.
   *
   * Not the same question as `live`, which is the status column and says
   * whether the account is switched on. An account can be Active and untouched
   * since April — 1,298 of 1,314 are Active, 352 have signed in this week —
   * and only one of those two numbers tells anybody whether the product is
   * being used. Both are kept because both get asked.
   */
  const dAct = act ? daysAgo(act) : null;
  const snoozed = con ? daysAgo(con) <= S.snoozeDays : false;
  const currency = S.currency || 'AED';

  const flags = [];
  if (!excluded) {
    if (S.flagNoOwner && !String(r.handler || '').trim()) {
      flags.push({ sev: 3, txt: 'No owner assigned', tag: 'unowned' });
    }
    if (S.flagNoTrialDate && (type === 'Trial' || String(r.plan || '').toUpperCase() === 'TRIAL') && !trial && !noTrial) {
      flags.push({ sev: 2, txt: 'Trial end not recorded', tag: 'notrialdate' });
    }
    if (dT !== null && dT < 0 && !paid && live) {
      flags.push({ sev: 3, txt: `Trial ended ${Math.abs(dT)}d ago, unpaid`, tag: 'trial-over' });
    } else if (dT !== null && dT <= 3 && dT >= 0 && !paid) {
      flags.push({ sev: 2, txt: `Trial ends in ${dT}d`, tag: 'trial-soon' });
    }
    /* A trial that is still running is not up for renewal: its expiry is
       usually its trial end, and it converts or lapses — the trial flags say
       which. Calling it "Renews in 14d" filled the Renewals list with every
       account that had signed up in the last fortnight. */
    const inTrial = trial && dT !== null && dT >= 0;
    if (dE !== null && dE < 0 && live) {
      flags.push({ sev: 3, txt: `Expired ${Math.abs(dE)}d ago`, tag: 'expired' });
    } else if (!inTrial && dE !== null && dE >= 0 && dE <= S.renewWindow) {
      flags.push({ sev: 2, txt: `Renews in ${dE}d`, tag: 'renewing' });
    }
    if (!paid && live && type === 'Paying') {
      flags.push({ sev: 2, txt: 'Payment not received', tag: 'unpaid' });
    }
    if (S.flagStale && live && act && daysAgo(act) > S.staleDays) {
      flags.push({ sev: 2, txt: `No activity in ${daysAgo(act)}d`, tag: 'stale' });
    }
    if (S.flagNoContact && !String(r.contact || '').trim()) {
      flags.push({ sev: 1, txt: 'No contact person', tag: 'nocontact' });
    }
  }

  const amt = amountOf(S, r);
  const next = nextDueOf(S, r);
  const dueIn = next ? daysTo(next) : null;
  const recurring = isBilled(S, r);

  if (!excluded && live && recurring && next) {
    if (dueIn < 0) {
      flags.push({ sev: 3, txt: `Payment overdue ${Math.abs(dueIn)}d — ${money(amt, currency)}`, tag: 'overdue' });
    } else if (dueIn <= (S.dueSoonDays || 5)) {
      flags.push({ sev: 2, txt: `${money(amt, currency)} due in ${dueIn}d`, tag: 'due-soon' });
    }
  }
  if (!excluded && live && paid && priceOf(S, r.plan) > 0 && !isBundled(S, r)
      && !moduleList(r).length && reg && daysAgo(reg) > (S.upsellDays || 60)) {
    flags.push({ sev: 1, txt: `No add-ons after ${daysAgo(reg)}d — upsell opportunity`, tag: 'upsell' });
  }
  if (!excluded && live && priceOf(S, r.plan) > 0 && !periodDays(r.cycle)) {
    flags.push({ sev: 2, txt: 'Paid plan with no billing cycle', tag: 'nocycle' });
  }
  /*
   * An amount that does not cover the cycle it is filed under.
   *
   * WOODBRIDGE MARINE is STARTER on Annual for 49, where the other STARTER
   * annual account pays 470 — one year's fee typed as one month's. It is a
   * fact from the clients app, so nothing here can correct it; but a figure
   * that quietly divides to AED 4.08 a month should be visible rather than
   * simply low. Only raised where the gap is large enough to be a mistake
   * rather than a negotiated price: under half of one cycle.
   */
  const shouldBe = planAmount(S, r);
  const stated = statedAmount(r);
  if (!excluded && live && paid && stated !== null && shouldBe > 0
      && cycleMonths(r.cycle) > 1 && stated < shouldBe / 2) {
    flags.push({
      sev: 2,
      tag: 'underpriced',
      txt: `${r.cycle} charge of ${money(stated, currency)} where the plan is ${money(shouldBe, currency)}`
    });
  }

  flags.sort((a, b) => b.sev - a.sev);

  /*
   * How long has money been outstanding? Dated from whichever deadline passed.
   *
   * Only when a deadline has passed. This used to fall back to the
   * registration date, which made every account not marked paid "unpaid" from
   * the day it signed up — every Free account, and every trial still inside
   * its fourteen days — and put the Payments list at a thousand rows of people
   * who owed nothing.
   */
  let since = null;
  if (!paid && live && !excluded && type !== 'Free') {
    if (exp && dE < 0) since = exp;
    else if (trial && dT < 0) since = trial;
    else if (recurring && next && dueIn < 0) since = next;
  }

  // Lifecycle bar: trial progress while in trial, otherwise subscription term.
  let pct = null;
  let pcls = '';
  if (trial && dT !== null) {
    pct = Math.max(0, Math.min(100, Math.round((S.trialDays - Math.max(dT, 0)) / S.trialDays * 100)));
    pcls = dT < 0 ? 'bad' : dT <= 3 ? 'warn' : '';
  } else if (reg && exp && dE !== null) {
    const total = Math.round((startOfDay(exp) - startOfDay(reg)) / 864e5);
    if (total > 0) {
      pct = Math.max(0, Math.min(100, Math.round((total - Math.max(dE, 0)) / total * 100)));
      pcls = dE < 0 ? 'bad' : dE <= S.renewWindow ? 'warn' : 'ok';
    }
  }

  return {
    type, excluded, reg, trial, exp, act, con, dT, dE, paid, live, flags, snoozed, dAct,
    noTrial, pct, pcls, amt, next, dueIn, recurring,
    unpaidDays: since ? daysAgo(since) : null
  };
}

/* --------------------------------------------------------------- filters */

/**
 * Is the row's registration date inside the window?
 *
 * The range always measures `regOn`. A row carries seven dates and an earlier
 * version let the user pick between them, but "when did these accounts come
 * in" is the question a client list is actually asked, and the picker cost a
 * control to answer it.
 *
 * Compared at day resolution so a `to` of today includes everything that
 * happened today, whatever the time of day or timezone offset the connector
 * sent. A row with no registration date cannot be shown to fall inside a
 * window, so it drops out — an empty list is worse than a wrong one.
 */
export function inDateRange(r, filters = {}) {
  const from = filters.from ? parseDate(filters.from) : null;
  const to = filters.to ? parseDate(filters.to) : null;
  if (!from && !to) return true;

  const on = parseDate(r.regOn);
  if (!on) return false;

  const day = startOfDay(on);
  if (from && day < startOfDay(from)) return false;
  if (to && day > startOfDay(to)) return false;
  return true;
}

/** Does a row survive the current search box, dropdowns and stat-tile flag? */
/* "Moved here by somebody", whichever board they were on at the time. Its own
   token rather than a blank, because a blank means no filter at all. */
export const CAME_ANY = '__moved';
/* The other side of the same question: the accounts that registered
   themselves in the clients app, which is nearly the whole book. */
export const CAME_SELF = '__registered';

/* "Through any partner" and "direct, no partner". Values rather than names so
   they cannot collide with a real partner called Any. */
export const PARTNER_ANY = '__any';
export const PARTNER_NONE = '__none';

export function matchesFilters(S, r, d, { filters, flag, view }) {
  const q = (filters.q || '').trim().toLowerCase();
  if (q) {
    const hay = [r.name, r.email, r.promo, r.handler, r.contact, r.plan, r.status, r.source, r.notes, r.cameFrom,
      r.partner, r.partnerVia]
      .join(' ').toLowerCase();
    if (!hay.includes(q)) return false;
  }

  for (const k of ['acctType', 'status', 'plan', 'pay', 'source', 'handler']) {
    if (filters[k] && String(k === 'acctType' ? d.type : (r[k] || '')) !== filters[k]) return false;
  }

  /*
   * The promo code, which is three questions rather than one.
   *
   * Four hundred and twenty-five of twelve hundred accounts carry one and
   * three hundred and fifty-nine of those are unique, so a plain list of
   * codes is a dropdown nobody can use. What people ask is "who came in on
   * a code at all", "who came in on none", and — for the dozen that were
   * used more than once and are therefore a channel rather than a one-off —
   * "who came in on this one".
   */
  if (filters.promo) {
    const code = String(r.promo || '').trim();
    if (filters.promo === PROMO_ANY && !code) return false;
    if (filters.promo === PROMO_NONE && code) return false;
    if (filters.promo !== PROMO_ANY && filters.promo !== PROMO_NONE
      && code.toUpperCase() !== filters.promo.toUpperCase()) return false;
  }

  /*
   * Where the account came from.
   *
   * Three answers, because that is the shape of the question: everything
   * somebody here carried across, or one of the two boards it came off. A
   * manager asking "how many did Geo take across" wants the first; a manager
   * comparing the pipeline against the deal board wants the other two.
   */
  /*
   * When they last signed in.
   *
   * A window rather than a date pair, because the question is always "in the
   * last N days" and making somebody work out what date that was is how a
   * filter goes unused. The two that are not windows are the two that matter
   * most: who has gone quiet, and who has never signed in at all.
   */
  if (filters.seen) {
    const n = d.dAct;
    if (filters.seen === SEEN_NEVER) { if (n !== null) return false; }
    else if (filters.seen === SEEN_COLD) { if (n === null || n <= 60) return false; }
    else if (n === null || n > Math.max(0, Number(filters.seen) - 1)) return false;
  }

  /*
   * Which partner brought them in.
   *
   * Three answers, like the promo filter beside it and for the same reason:
   * "anyone at all" and "nobody" are the two that are always worth asking,
   * and the named partners are the ones worth a row of their own.
   */
  if (filters.partner) {
    const who = String(r.partner || '').trim();
    if (filters.partner === PARTNER_ANY) { if (!who) return false; }
    else if (filters.partner === PARTNER_NONE) { if (who) return false; }
    else if (who !== filters.partner) return false;
  }

  if (filters.cameFrom) {
    const from = String(r.cameFrom || '');
    if (filters.cameFrom === CAME_ANY ? !from
      : filters.cameFrom === CAME_SELF ? !!from
        : from !== filters.cameFrom) return false;
  }

  if (!inDateRange(r, filters)) return false;

  const f = flag || PRESET_FLAGS[view] || '';
  if (!f) return true;

  if (f === 'all-live') return d.live && !d.excluded;
  if (f === 'in-trial') return d.dT !== null && d.dT >= 0;
  if (f === 'trial-soon') return d.flags.some(x => x.tag === 'trial-soon' || x.tag === 'trial-over');
  if (f === 'unpaid') return d.unpaidDays !== null;
  /* Money actually recorded against them, not a plan chosen: "payment done"
     is the question, and an account can sit on STARTER for a year without
     one. */
  if (f === 'paid') return !!d.paid;
  if (f === 'renewing') return d.flags.some(x => x.tag === 'renewing' || x.tag === 'expired');
  /*
   * Just lapsed, and only just.
   *
   * Expiry has passed and it passed inside the last fortnight. Renewals was
   * everything ever expired plus everything still running — a thousand rows
   * with the dozen worth ringing somewhere inside it. The point of this
   * screen is the window where a lapsed account can still be won back.
   */
  if (f === 'expired-14') return d.dE !== null && d.dE < 0 && d.dE >= -LAPSED_DAYS;
  if (f === 'stale') return d.flags.some(x => x.tag === 'stale');
  if (f === 'age1') return d.unpaidDays !== null && d.unpaidDays <= S.ageMid;
  if (f === 'age2') return d.unpaidDays !== null && d.unpaidDays > S.ageMid && d.unpaidDays <= S.ageLate;
  if (f === 'age3') return d.unpaidDays !== null && d.unpaidDays > S.ageLate;
  return true;
}

const DATE_KEYS = ['regOn', 'trialEnd', 'expiry', 'lastActivity', 'lastContacted', 'nextDue', 'lastPaid'];

/** Filter, then sort. Blank dates always sink to the bottom either way. */
export function visibleClients(S, rows, ui) {
  const out = rows.filter(r => matchesFilters(S, r, deriveClient(S, r), ui));
  const { k, dir } = ui.sort;

  return out.sort((a, b) => {
    if (k === '__trial') {
      const x = deriveClient(S, a).dT;
      const y = deriveClient(S, b).dT;
      return (x === null ? 1e9 : x) - (y === null ? 1e9 : y);
    }

    let x = a[k];
    let y = b[k];

    if (DATE_KEYS.includes(k)) {
      const px = parseDate(x);
      const py = parseDate(y);
      x = px ? px.getTime() : (dir === 1 ? Infinity : -Infinity);
      y = py ? py.getTime() : (dir === 1 ? Infinity : -Infinity);
    } else if (k === 'sno') {
      x = +x || 0;
      y = +y || 0;
    } else {
      x = String(x || '').toLowerCase();
      y = String(y || '').toLowerCase();
    }

    return x < y ? -dir : x > y ? dir : 0;
  });
}

/**
 * What each tile counts — the rule itself, not a copy of it.
 *
 * Every tile had its count here and its list in ClientStatView, written
 * separately, and four of them had drifted: Unpaid counted 17 and listed
 * 20 because the list forgot the account had to be live; Renewals counted
 * the expired and listed only the renewing; In trial counted the ones
 * still running and listed everybody who had ever had one. A tile whose
 * popup disagrees with it is worse than no tile — it is a number nobody
 * can trust again once they have seen it happen.
 *
 * So the rule lives once and both read it. A tile and its list cannot
 * disagree if there is only one of them.
 */
/*
 * How recently somebody has to have signed in to count as active.
 *
 * Seven days, because that is what the team means by the word: an account
 * that has not been opened in a fortnight is not an active customer whatever
 * its status column says. The longer windows are the ones people ask for when
 * they are sizing a problem rather than counting today.
 */
export const SEEN_DAYS = 7;
export const SEEN_WINDOWS = [7, 14, 30, 60];
/* "Not in sixty days", and "the app has never seen them". Values rather than
   numbers so they cannot be mistaken for a window. */
export const SEEN_COLD = '__cold';
export const SEEN_NEVER = '__never';

export const TILE = {
  total: x => !x.d.excluded,
  live: x => !x.d.excluded && x.d.live,
  /*
   * Active means somebody has used it, not that the row says Active.
   *
   * The tile counted the status column and read 1,298 of 1,314 — a number
   * that cannot move and therefore cannot be acted on. Signed in within the
   * week is 352, which is the figure the team was already quoting in
   * meetings and could not find on this screen.
   */
  active: x => !x.d.excluded && x.d.dAct !== null && x.d.dAct <= SEEN_DAYS,
  trial: x => x.d.dT !== null && x.d.dT >= 0,
  soon: x => x.d.flags.some(f => f.tag === 'trial-soon' || f.tag === 'trial-over'),
  /*
   * Owed, not merely unpaid.
   *
   * It was `unpaidDays !== null`, which is every live account that has not
   * paid — and 1,183 of the 1,227 are on FREE and were never going to. The
   * tile read 1,202 of 1,228, which is not a number anybody can act on and,
   * beside a Payments menu showing 18, reads like a contradiction.
   *
   * Somebody owes when a cycle and an amount say they were billed and the
   * payment has not landed. A free account is not behind on anything.
   */
  /*
   * Behind on something, not merely "has never paid us".
   *
   * It was `unpaidDays !== null`, which is every live account that has not
   * paid — and 1,183 of the 1,227 are on FREE and were never going to. The
   * tile read 1,202 of 1,228, which is not a number anybody can act on and,
   * beside a Payments menu showing 18, reads like a contradiction.
   *
   * Requiring an amount as well was too far the other way: the clients app
   * sends 0 for most accounts, so that emptied the list. What is left is the
   * honest middle — somebody who is on the book as a trial or a paying plan,
   * is still live, and has no payment against them. A free account is not
   * behind on anything and is not on this list.
   */
  unpaid: x => x.d.unpaidDays !== null && !x.d.paid && !x.d.excluded
    && x.d.live && x.d.type !== 'Free',
  /* Money actually recorded. The same test the Payments menu runs, so the
     tile and the menu cannot say different things about the same word. */
  paid: x => !!x.d.paid,
  /*
   * Just lapsed, and only just — the Renewals menu's rule, not a second one.
   *
   * It was "renewing or ever expired", which put 36 on a dashboard tile
   * beside a menu badge reading 4 and a page listing 4. One word cannot
   * carry two numbers; whichever is right, both have to be it.
   */
  renew: x => x.d.dE !== null && x.d.dE < 0 && x.d.dE >= -LAPSED_DAYS,
  stale: x => x.d.flags.some(f => f.tag === 'stale'),
  open: x => x.d.flags.length && !x.d.snoozed,
  /* The two billing tiles show money; their lists show who it comes
     from, which is everybody on that cycle who is counted at all. */
  monthly: x => !x.d.excluded && String(x.r.cycle || '').toLowerCase() === 'monthly',
  annual: x => !x.d.excluded && String(x.r.cycle || '').toLowerCase() === 'annual',
  /* Conversion is a rate, so its list is the numerator: everybody who
     finished a trial and went on to pay. */
  conv: x => x.d.trial && x.d.dT < 0 && !x.d.excluded && x.d.paid
};

/** The headline counts every dashboard tile reads from. */
/**
 * How many rows a client view will actually list.
 *
 * The badge beside a menu is a promise about the page behind it, and the two
 * were built from different rules: the sidebar read the dashboard's tiles, so
 * Payments said 1,202 (everybody who has *not* paid) over a page listing the
 * 18 who have, and Renewals said 36 over a page listing 4. A number that
 * disagrees with its own page is worse than no number — it is the one people
 * stop believing first, and then they stop believing the rest.
 *
 * So it runs the same `matchesFilters` the list runs, with the view's preset
 * flag and nothing else. Whatever the page decides, the badge says.
 */
export function viewCount(S, rows, view) {
  return rows.filter(r => matchesFilters(S, r, deriveClient(S, r), {
    filters: {}, flag: '', view
  })).length;
}

/**
 * All four client menu badges, in one walk of the book.
 *
 * `viewCount` was called once per badge straight out of the sidebar's render —
 * four walks of 1,228 accounts, each one running `deriveClient` over every
 * row, on every render. The sidebar reads the whole store, so it re-rendered
 * on every keystroke in a search box: 79ms of arithmetic between pressing a
 * key and seeing the letter.
 *
 * One walk now, and `deriveClient` once per account rather than four times.
 * The caller memoises it on [S, rows], so it runs when the book changes
 * instead of when anything at all changes.
 */
export function viewCounts(S, rows) {
  const out = { clients: 0, trials: 0, payments: 0, renewals: 0 };
  const views = Object.keys(out);
  rows.forEach(r => {
    const d = deriveClient(S, r);
    views.forEach(view => {
      if (matchesFilters(S, r, d, { filters: {}, flag: '', view })) out[view] += 1;
    });
  });
  return out;
}

export function ledgerStats(S, rows) {
  const pairs = rows.map(r => ({ r, d: deriveClient(S, r) }));
  const included = pairs.filter(x => !x.d.excluded);
  const finished = pairs.filter(x => x.d.trial && x.d.dT < 0 && !x.d.excluded);
  const converted = finished.filter(x => x.d.paid);

  const n = key => pairs.filter(TILE[key]).length;

  return {
    pairs,
    included,
    total: n('total'),
    live: n('live'),
    active: n('active'),
    trial: n('trial'),
    soon: n('soon'),
    unpaid: n('unpaid'),
    paid: n('paid'),
    renew: n('renew'),
    stale: n('stale'),
    open: n('open'),
    conv: finished.length ? Math.round(converted.length / finished.length * 100) : null,
    convW: converted.length,
    convN: finished.length
  };
}

/**
 * The acquisition funnel, read straight off the account records.
 *
 * Where `ledgerStats` answers "what needs attention today", this answers
 * "where is the business coming from". The Sales overview reads it because a
 * connected clients app carries accounts and nothing else — no deals, no
 * stages, no won and lost — so the funnel is the only pipeline that can be
 * live. Accounts are segmented on `acctType` rather than the derived flags so
 * the numbers match what the clients app itself reports.
 */
export function funnelStats(S, rows) {
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const typeOf = r => String(r.acctType || '').toLowerCase();
  const count = t => rows.filter(r => typeOf(r) === t).length;

  const paying = count('paying');
  const demo = count('demo');
  const trial = count('trial');

  const joined = rows.filter(r => {
    const d = parseDate(r.regOn);
    return d && d >= monthStart;
  }).length;

  return {
    accounts: rows.length,
    free: count('free'),
    demo,
    trial,
    paying,
    // Everyone being actively worked: the closest thing to an open pipeline.
    inFlight: demo + trial,
    joined,
    // One decimal place. At 14 paying in 761 a whole number reads as "2%" and
    // would hide every improvement until another fifteen accounts convert.
    conv: rows.length ? Math.round((paying / rows.length) * 1000) / 10 : null
  };
}

/** Payments recorded against a client, optionally only since a date. */
export const collected = (r, from) =>
  (r.pays || []).reduce((t, p) => {
    const d = parseDate(p.d);
    return t + ((!from || (d && d >= from)) ? (parseFloat(p.amt) || 0) : 0);
  }, 0);

/**
 * Record a payment: stamp today, roll the due date forward one cycle, mark the
 * client active. Returns the updated row and the amount taken, or `null` when
 * there is nothing to charge.
 */
export function applyPayment(S, row) {
  const r = { ...row };
  if (!periodDays(r.cycle)) r.cycle = 'Monthly';

  const amount = amountOf(S, r);
  if (!amount) return null;

  const now = new Date();
  const stamp = formatDate(now, false, S.dateFmt);
  const due = parseDate(r.nextDue);

  r.pays = [...(r.pays || []), { d: stamp, amt: amount, plan: r.plan, cycle: r.cycle }];
  r.lastPaid = stamp;
  r.pay = 'YES';
  if (!r.amount) r.amount = String(amount);
  r.nextDue = formatDate(
    addPeriod(due && startOfDay(due) > startOfDay(now) ? due : now, r.cycle), false, S.dateFmt);
  if (String(r.status || '').toLowerCase() !== 'active') r.status = 'Active';

  return { row: r, amount };
}

/** Collections summary for the Billing screen. */
export function billingStats(S, rows) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  let mrr = 0;
  let risk = 0;
  let dueMonth = 0;
  let overdue = 0;
  let got = 0;
  let due30 = 0;

  rows.forEach(r => {
    const d = deriveClient(S, r);
    if (d.excluded || !d.live) return;

    const m = mrrOf(S, r, d);
    if (d.paid) mrr += m;
    if (d.flags.length) risk += m;
    got += collected(r, monthStart);

    /* Same gate as the Billing schedule: a free account reaching its signup
       anniversary is not money due. See the note in Billing.jsx. */
    if (d.type !== 'Paying' || !isBilled(S, r) || !d.next) return;
    const amount = amountOf(S, r);
    if (d.next.getMonth() === now.getMonth() && d.next.getFullYear() === now.getFullYear()) {
      dueMonth += amount;
    }
    if (d.dueIn < 0) overdue += amount;
    if (d.dueIn >= 0 && d.dueIn <= 30) due30 += amount;
  });

  return { mrr, arr: mrr * 12, dueMonth, overdue, got, due30, risk };
}
